import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import PropTypes from 'prop-types';
import CompareIcon from 'calcite-ui-icons-react/CompareIcon';
import ReactTooltip from 'react-tooltip';
import config from '../../config/config';

import LeftPanel from '../leftpanel/leftpanel';
import Timeline from '../timeline/timeline';
import Loader from '../leftpanel/loader';
import MapLayers from '../leftpanel/leftpanel-components/map-layers';
import ReportModal from '../modals/report-modal/report-modal';
import LayerModal from '../modals/layer-modal/layer-modal';
import InfoModal from '../modals/info-modal/info-modal';

import MapController from '../../controllers/MapController';
import SketchController from '../../controllers/SketchContoller';
import HansenChartController from '../../controllers/HansenChartController';
import { RootState } from '../../reducers/app';
import './mapview.scss';
import { ILandsatLayerResult, ISentinelLayerResult } from '../../types/layers';
import { toggleMapLoader } from '../../reducers/mapview/actions';
import WidgetContainer from './widget-container';
import { toggleToastError } from '../../reducers/mapview/actions';
import { IReportChart } from '../../types/report';

import { Resources } from '../modals/resources/resources';
import ga, { GACategories, GAActions } from '../../utils/googleAnalytics.utils';
/**
 * Component for displaying the Map and Map UI
 */
const MapView = ({ setShowTour }: { setShowTour: Function }) => {
  // LOCAL STATE
  const [basemapSelectorVisible, setBasemapSelectorVisible] = useState(false);
  const [elevationProfileVisible, setElevationProfileVisible] = useState(false);
  const [areaMeasurementVisible, setAreaMeasurementVisible] = useState(false);
  const [distanceMeasurementVisible, setDistanceMeasurementVisible] = useState(false);
  const [mapLayersToggled, setMapLayersToggled] = useState(true);
  const [landsatResults, setLandsatResults] = useState<Array<ILandsatLayerResult> | undefined>(undefined);
  const [sentinelResults, setSentinelResults] = useState<Array<ISentinelLayerResult> | undefined>(undefined);
  const [swipeReady, setSwipeReady] = useState(false);
  const [swipeVisible, setSwipeVisible] = useState(false);
  const [hasTimelineResults, setHasTimelineResults] = useState(false);
  const [reportMap, setReportMap] = useState<string>('');
  const [reportCharts, setReportCharts] = useState<Array<IReportChart>>([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [infoModalActive, setInfoModalActive] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [resourceList, setResourceList] = useState<any>();

  const dispatch = useDispatch();
  const { addToast } = useToasts();

  // REDUX
  const error = useSelector((state: RootState) => state.mapview.error.msg);
  const mapReady = useSelector((state: RootState) => state.mapview.mapReady);
  const drawComplete = useSelector((state: RootState) => state.mapview.drawComplete);
  const timelineOpen = useSelector((state: RootState) => state.auditdata.timelineOpen);
  const annotationToolActive = useSelector((state: RootState) => state.mapview.annotationToolActive);
  const imagePairs = useSelector((state: RootState) => state.auditdata.selectedImagePairs.imagePairs);
  const mapLoaderActive = useSelector((state: RootState) => state.mapview.loader);
  const leftPanelLoaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const layerModal = useSelector((state: RootState) => state.mapview.layerModal);
  const tempGraphic = useSelector((state: RootState) => state.mapview.tempGraphic);
  const swipeToolActive = useSelector((state: RootState) => state.mapview.swipeToolActive);
  const performAuditLayers = useSelector((state: RootState) => state.mapview.performAuditLayers);
  const myLayers = useSelector((state: RootState) => state.mapview.myLayers);
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);
  const auditArea = useSelector((state: RootState) => state.auditdata.auditArea);
  const auditTreeLoss = auditArea.treeLoss ? auditArea.treeLoss[auditArea.treeLoss.length - 1] : 0;
  console.log("audit area gr :: ", auditArea.graphic);
  
  
  // handle uploaded kml with no extent
  const auditCoordinates = auditArea.graphic?.geometry.extent.center;

  // REFS
  const mapviewEl = useRef(null);

  // To ensure the report component doesn't re-render while it's being built (which can break things), we memoize the checklist of what's needed for the report to be ready)
  const checkReportIsReady = () => {
    const checkList = [
      !leftPanelLoaderActive && !mapLoaderActive,
      reportMap,
      reportCharts,
      performAuditLayers,
      visibleLayers,
      reportReady,
      auditArea.size > 0 ? auditCoordinates : true,
    ];
    return checkList.every((item) => !!item);
  };

  const reportClearedForRender = useMemo(
    () => checkReportIsReady(),
    // we don't need checkReportIsReady in this list, so ignoring eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      reportMap,
      reportCharts,
      leftPanelLoaderActive,
      mapLoaderActive,
      performAuditLayers,
      visibleLayers,
      auditCoordinates,
      auditTreeLoss,
      reportReady,
    ]
  );

  const toggleMeasurementWidget = (measurementType: any) => {
    // Disable the appropriate widget if it is enabled
    if (distanceMeasurementVisible || areaMeasurementVisible) {
      setAreaMeasurementVisible(false);
      setDistanceMeasurementVisible(false);
      MapController._areaMeasurementWidget?.viewModel.clear();
      MapController._distanceMeasurementWidget?.viewModel.clear();
      MapController.activeMeasurementWidget = null;
    } else {
      switch (measurementType) {
        case 'distance':
          setDistanceMeasurementVisible(!distanceMeasurementVisible);
          MapController._distanceMeasurementWidget?.viewModel.start();
          MapController.activeMeasurementWidget = 'distance';
          if (distanceMeasurementVisible) {
            ga.event(GACategories.Widget, GAActions.Opened, 'Distance Measurement Widget');
          }
          break;
        case 'area':
          setAreaMeasurementVisible(!areaMeasurementVisible);
          MapController._areaMeasurementWidget?.viewModel.start();
          MapController.activeMeasurementWidget = 'area';
          if (areaMeasurementVisible) {
            ga.event(GACategories.Widget, GAActions.Opened, 'Area Measurement Widget');
          }
          break;
        default:
          setAreaMeasurementVisible(false);
          setDistanceMeasurementVisible(false);
          MapController.activeMeasurementWidget = null;
      }
    }
  };

  const toggleSwipeWidget = () => {
    if (imagePairs.length > 0) {
      MapController.toggleSwipeWidget(swipeVisible);
      setSwipeVisible(!swipeVisible);
      if (swipeVisible) {
        ga.event(GACategories.Widget, GAActions.Opened, 'Swipe Widget');
      }
    }
  };

  const toggleAnnotationTool = () => {
    SketchController.toggleAnnotationSketchTool(!annotationToolActive);
    if (annotationToolActive) {
      ga.event(GACategories.Widget, GAActions.Opened, 'Annotations Tool');
    }
}

  const generateReport = async () => {
    ga.event(GACategories.Widget, GAActions.Opened, 'Create Report');          
    if (reportReady) {
      setReportReady(false);
    }
    setGeneratingReport(true);
    const map = await MapController.printMap();
    if (map) {
      setReportMap(map);
    }

    const chartsToPrint = await HansenChartController.prepareChartsToPrint(performAuditLayers);
    setReportCharts(chartsToPrint);
    if ((map && map !== '') || (chartsToPrint && chartsToPrint.length > 0)) {
      setReportReady(true);
    } else {
      addToast('Report generation failed, please try again.', { appearance: 'error', autoDismiss: true });
      setGeneratingReport(false);
    }
  };

  const toggleBasemap = () => {
    setBasemapSelectorVisible(!basemapSelectorVisible);
    if (basemapSelectorVisible) {
      ga.event(GACategories.Widget, GAActions.Opened, 'Basemap Selector');
    }
  }

  const toggleElevationProfileTool = () => {
    setElevationProfileVisible(!elevationProfileVisible);
    if (elevationProfileVisible) {
      ga.event(GACategories.Widget, GAActions.Opened, 'Elevation Profile');
    }
  }

  useEffect(() => {
    if (myLayers.length > 0 && myLayers[myLayers.length - 1].id.includes('-raster-analysis') && !mapLayersToggled) {
      setMapLayersToggled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLayers, myLayers.length]);

  useEffect(() => {
    MapController.initializeMap(mapviewEl);
    const params = {
      where: '1=1',
      outFields: ['*'],
    };
    const fetchResources = async () => {
      const res = await MapController.esriQuery(
        config.esri.portalResourcesURL,
        params
      );
      if (res?.features?.length) setResourceList(res);
    };
    fetchResources();
  }, []);

  useEffect(() => {
    if (error && error !== '') {
      addToast(error, { appearance: 'error', autoDismiss: true });
      dispatch(toggleToastError(false, '')); //reset Redux error state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Runs whenever the user is done drawing their Audit Area (FMU)
  useEffect(() => {
    if (drawComplete) {
      const getAuditAreaResults = async () => {
        dispatch(toggleMapLoader(true));
        const results = await MapController.getAuditAreaResults();
        setSentinelResults(results.sentinelResults);
        setLandsatResults(results.landsatResults);
        setHasTimelineResults(true);
        dispatch(toggleMapLoader(false));
      };
      getAuditAreaResults();
    } else {
      if (hasTimelineResults) {
        setHasTimelineResults(false);
        setSentinelResults(undefined);
        setLandsatResults(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawComplete]);

  useEffect(() => {
    if (imagePairs.length > 0) {
      setSwipeReady(true);
    } else {
      setSwipeReady(false);
      setSwipeVisible(false);
      MapController._swipe?.destroy();
    }
  }, [imagePairs]);

  // Handles any cancelling of the SwipeTool caused by errors retrieving Images
  useEffect(() => {
    if (!swipeToolActive && swipeVisible) {
      toggleSwipeWidget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeToolActive]);

  useEffect(() => {
    if (swipeToolActive) {
      if (timelineOpen) {
        setSwipeVisible(false);
        setSwipeReady(false);
        MapController.toggleSwipeWidget(false);
      } else {
        setSwipeReady(true);
        toggleSwipeWidget();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineOpen])

  useEffect(() => {
    if (reportReady) return;
    setReportMap('');
  }, [reportReady]);

  return (
    <div className='mapview-container'>
      {mapLoaderActive && <Loader />}
      <React.Fragment>
        <div className='mapview' ref={mapviewEl} />
        <div id='search' data-tut='step-seven' />
        <div
          data-tip='Change Basemap'
          data-html={true}
          data-for='tooltip'
          id={`basemap-selector`}
          className={`toggle-basemap-selector ${
            basemapSelectorVisible ? ' selected' : ''
          } esri-widget-button esri-widget esri-interactive esri-component`}
          onClick={() => toggleBasemap()}
        >
          <span className='esri-icon-basemap' />
        </div>
        <div id='measurement-widgets-container' className='measurement-widgets-container' data-tut='step-eight'>
          <div
            data-tip='Measure Distance'
            data-html={true}
            data-for='tooltip'
            id={`distance-measurement-selector`}
            className={`toggle-distance-measurement-selector no-bottom-margin ${
              distanceMeasurementVisible ? ' selected' : ''
            } esri-widget-button esri-widget esri-interactive esri-component`}
            onClick={() => toggleMeasurementWidget('distance')}
          >
            <span className='esri-icon-measure' />
          </div>
          <div
            data-tip='Measure Area'
            data-html={true}
            data-for='tooltip'
            id={`area-measurement-selector`}
            className={`toggle-area-measurement-selector no-bottom-margin ${
              areaMeasurementVisible ? ' selected' : ''
            } esri-widget-button esri-widget esri-interactive esri-component`}
            onClick={() => toggleMeasurementWidget('area')}
          >
            <span className='esri-icon-measure-area' />
          </div>
        </div>
        <div
          data-tip='Create Annotations'
          data-html={true}
          data-for='tooltip'
          id={`annotation-selector`}
          className={`toggle-annotation-selector ${
            annotationToolActive ? ' selected' : ''
          } esri-widget-button esri-widget esri-interactive esri-component`}
          onClick={() => toggleAnnotationTool()}
          data-tut='step-nine'
        >
          <span className='esri-icon-comment' />
        </div>
        <div
          data-tip='Elevation Profile Tool'
          data-html={true}
          data-for='tooltip'
          id={`elevation-profile-tool`}
          className={`toggle-elevation-profile-tool ${
            elevationProfileVisible ? ' selected' : ''
          } esri-widget-button esri-widget esri-interactive esri-component`}
          onClick={() => toggleElevationProfileTool()}
        >
          <span className='esri-icon-polyline' />
        </div>
        <div
          data-tip='Create Report'
          data-html={true}
          data-for='tooltip'
          id={`print-map-selector`}
          className={`${
            leftPanelLoaderActive || mapLoaderActive ? 'disabled' : ''
          } toggle-print-map-selector esri-widget-button esri-widget esri-interactive esri-component`}
          onClick={() => generateReport()}
          data-tut='step-ten'
        >
          <span className='esri-icon-printer' />
        </div>
        <div
          data-tip='Swipe Between Audit Periods'
          data-html={true}
          data-for='tooltip'
          id={`swipe-selector`}
          className={`${swipeReady ? '' : 'disabled'} ${
            swipeVisible ? 'selected' : ''
          } toggle-swipe-selector esri-widget-button esri-widget esri-interactive esri-component`}
          onClick={() => toggleSwipeWidget()}
          data-tut='step-eleven'
        >
          <div className='swipe-icon-size'>
            <CompareIcon size={16} />
          </div>
        </div>
        <WidgetContainer
          styles={{width: '300px'}}
          visible={elevationProfileVisible}
          classNames='widget-selector-container'
          ids='elevation-profile-div'
        />
        <WidgetContainer visible={basemapSelectorVisible} classNames='widget-selector-container' ids='basemap-div' />
        <WidgetContainer
          visible={distanceMeasurementVisible}
          classNames='widget-selector-container distance-selector'
          ids='distance-container-div'
        />
        <WidgetContainer
          visible={areaMeasurementVisible}
          classNames='widget-selector-container area-selector'
          ids='area-container-div'
        />
        {mapReady && (
          <LeftPanel
            timelineHasData={hasTimelineResults}
            setMapLayersToggled={() => setMapLayersToggled(!mapLayersToggled)}
            mapLayersToggled={mapLayersToggled}
            setInfoModalActive={() => setInfoModalActive(!infoModalActive)}
            setShowTour={setShowTour}
            setIsResourcesOpen={setIsResourcesOpen}
            isResourcesOpen={isResourcesOpen}
          />
        )}
        {mapReady && <MapLayers mapLayersToggled={mapLayersToggled} />}
        <Timeline open={timelineOpen} landsatResults={landsatResults} sentinelResults={sentinelResults} />
        {layerModal && layerModal.active && (
          <LayerModal
            layerTitle={layerModal.layerTitle}
            tempGraphic={tempGraphic}
            deleteLayer={layerModal.deleteLayer}
          />
        )}
        {infoModalActive && <InfoModal closeModal={() => setInfoModalActive(false)} />}
        {generatingReport && (
          <ReportModal
            setReportReady={setReportReady}
            performAuditLayers={performAuditLayers}
            auditAreaCoordinates={auditCoordinates}
            auditArea={auditArea}
            visibleLayers={visibleLayers}
            auditTreeLoss={auditTreeLoss}
            map={reportMap}
            charts={reportCharts}
            closeModal={setGeneratingReport}
            reportReady={reportClearedForRender}
          />
        )}
        {isResourcesOpen && resourceList && (
          <Resources setIsResourcesOpen={setIsResourcesOpen} resourceList={resourceList} />
        )}

        <ReactTooltip className='tooltip' id='tooltip' place='left' type='success' effect='solid' clickable={false} />
      </React.Fragment>
    </div>
  );
};

MapView.propTypes = {
  mapviewEl: PropTypes.instanceOf(Element),
};

export default MapView;
